import { ChevronDown, ChevronRight, ClipboardList, Loader2 } from 'lucide-react';
import { useCallback, useEffect, useState } from 'react';
import { Button } from '../ui/button';
import { Card } from '../ui/card';
import { toast } from '../ui';
import {
  bedAdminErrorMessage,
  getCrewBedAnswers,
  type BedAnswersResult,
} from '../../lib/bed/admin';
import { formatDate } from '../../lib/date-utils';

/**
 * 00253 (retour 30, recette 4L) : les réponses saisies dans le bulletin
 * d'engagement, consultables depuis la fiche équipage — par étape, avec le rôle.
 * Chargées à l'ouverture seulement (la fiche reste légère).
 */
const STEP_LABELS: Record<number, string> = {
  1: 'Équipage',
  2: 'Pilote',
  3: 'Copilote',
  4: 'Véhicule',
  5: 'Options et consentements',
  6: 'Récapitulatif',
};

export function BedAnswersCard({
  crewId,
  defaultOpen = false,
}: {
  crewId: string;
  /** Recette 4L du 22/09 (retour 29) : déplié d'emblée dans l'onglet dédié. */
  defaultOpen?: boolean;
}) {
  const [open, setOpen] = useState(defaultOpen);
  const [loading, setLoading] = useState(false);
  const [answers, setAnswers] = useState<BedAnswersResult | null>(null);

  const load = useCallback(async () => {
    setLoading(true);
    const result = await getCrewBedAnswers(crewId);
    if (result.status !== 'ok') {
      toast.error(bedAdminErrorMessage(result.status));
      setOpen(false);
    } else {
      setAnswers(result.data);
    }
    setLoading(false);
  }, [crewId]);

  // Revue du 23/09 (point 2) : dépliée d'emblée, la carte doit charger d'elle-
  // même — sinon l'onglet s'ouvrait vide, et il fallait replier puis déplier.
  useEffect(() => {
    if (defaultOpen) void load();
  }, [defaultOpen, load]);

  function toggle() {
    const next = !open;
    setOpen(next);
    if (next && answers === null && !loading) void load();
  }

  const rows = answers?.rows ?? [];
  const steps = [...new Set(rows.map((r) => r.step))].sort((a, b) => a - b);

  return (
    <Card className="p-5">
      <button
        type="button"
        onClick={toggle}
        className="flex w-full items-center justify-between text-left"
        aria-expanded={open}
      >
        <span className="flex items-center gap-2 text-base font-semibold text-zinc-900">
          <ClipboardList className="size-4 text-zinc-500" />
          Réponses du bulletin
        </span>
        {open ? (
          <ChevronDown className="size-4 text-zinc-400" />
        ) : (
          <ChevronRight className="size-4 text-zinc-400" />
        )}
      </button>

      {open && (
        <div className="mt-4">
          {loading ? (
            <p className="flex items-center gap-2 text-sm text-zinc-500">
              <Loader2 className="size-4 animate-spin" /> Chargement des réponses…
            </p>
          ) : answers && !answers.found ? (
            <p className="text-sm text-zinc-400">Aucun bulletin renseigné pour cet équipage.</p>
          ) : answers ? (
            <div className="space-y-4">
              <p className="text-xs text-zinc-500">
                {answers.submitted_at
                  ? `Bulletin soumis le ${formatDate(answers.submitted_at)}`
                  : 'Bulletin en cours de saisie'}
                {answers.updated_at && (
                  <> · dernière modification le {formatDate(answers.updated_at)}</>
                )}
              </p>
              {steps.map((step) => (
                <div key={step}>
                  <h4 className="mb-1.5 text-sm font-semibold text-zinc-900">
                    {STEP_LABELS[step] ?? `Étape ${step}`}
                  </h4>
                  <dl className="divide-y divide-zinc-100 rounded-md border border-zinc-200">
                    {rows
                      .filter((r) => r.step === step)
                      .map((r) => (
                        <div
                          key={r.field_id}
                          className="grid grid-cols-[1fr_1.2fr] gap-3 px-3 py-1.5 text-sm"
                        >
                          <dt className="text-zinc-600">
                            {r.label}
                            {r.role !== 'common' && (
                              <span className="ml-1 text-xs text-zinc-400">({r.role_label})</span>
                            )}
                          </dt>
                          <dd className={r.value ? 'text-zinc-900' : 'text-zinc-300'}>
                            {r.field_type === 'checkbox'
                              ? r.value
                                ? 'Oui'
                                : 'Non'
                              : r.value || '—'}
                          </dd>
                        </div>
                      ))}
                  </dl>
                </div>
              ))}
              <Button
                size="sm"
                variant="ghost"
                onClick={async () => {
                  setLoading(true);
                  const result = await getCrewBedAnswers(crewId);
                  if (result.status === 'ok') setAnswers(result.data);
                  setLoading(false);
                }}
              >
                Actualiser
              </Button>
            </div>
          ) : null}
        </div>
      )}
    </Card>
  );
}
